import React from 'react';
import { useTranslation } from 'react-i18next';

export interface BillingWorkspaceLayoutProps {
  children: React.ReactNode;
  description?: string;
}

/**
 * Shared chrome for carrier-dashboard billing pages.
 *
 * Width is capped at `max-w-6xl` (~1152px) and centered. The cap is
 * deliberately on the layout — not the individual cards — so both the
 * overview (single column) and the checkout (12-col grid with a
 * 3-across plan-card row on `xl`) get the same focused reading width
 * on large screens, and so dropping additional billing pages into
 * the workspace doesn't require each page to remember to cap itself.
 *
 * The cap is sized for the checkout: at `max-w-6xl` the 8/12 left
 * column lands around 768px, leaving ~256px per plan card in the
 * `xl:grid-cols-3` tier — anything narrower squeezes the cards.
 */
export function BillingWorkspaceLayout({ children, description }: BillingWorkspaceLayoutProps) {
  const { t } = useTranslation('m-billing');
  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        {description ?? t('page.description')}
      </p>
      {children}
    </div>
  );
}
